import { ComponentRef, Directive, OnInit, ViewContainerRef, inject, input } from '@angular/core';

import { IConfig, DynamicElementService } from '../models';
import { setComponentInputs } from '../utils/set-component-inputs';

/**
 * Renders one component looked up from the `DynamicElementService` registry by
 * the `componentName` field of its `config` input.
 *
 * The base directive is generic on `C extends IConfig` so subclasses can narrow
 * the config shape (see `DynamicFormElementDirective<IFormElementBaseConfig>`)
 * without re-declaring the input.
 */
@Directive({
    standalone: true,
    selector: '[siriusDynamicElement]',
})
export class DynamicElementDirective<C extends IConfig = IConfig> implements OnInit {
    readonly config = input.required<C>();

    private _viewContainerRef = inject(ViewContainerRef);
    private _dynamicElementService = inject(DynamicElementService);

    ngOnInit(): void {
        this.createComponent(this.config());
    }

    createComponent(config: C): ComponentRef<unknown> {
        const dynamicComponent = this._dynamicElementService.getComponent(config.componentName);

        if (!dynamicComponent) {
            throw new Error('Using unsupported type: ' + config.componentName);
        }

        const component = this._viewContainerRef.createComponent(dynamicComponent.component);
        setComponentInputs(component, { config });

        return component;
    }
}
